import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Tooltip from 'components/Tooltip';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { expandDataProps } from 'utils/helper/common';

import './tabs-section.scss';

import type { TabsSectionItem, TabsSectionTooltip } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  items: Array<TabsSectionItem>,
  activeTab: string,
  size: 'small' | 'normal',
  withShadow?: boolean,
  isDisabled?: boolean,
  className?: string,
  clickableActiveTab?: boolean,
  onClick: (
    d: string | undefined | null,
    c: string | undefined | null,
    b: boolean,
    a?: string | null
  ) => void
};

const COMMON_MSG_KEY = 'collabary_common';

class TabsSection extends React.PureComponent<Props> {
  static defaultProps = {
    size: 'normal',
    withShadow: false
  };

  getUpdatedItems(): Array<TabsSectionItem> {
    const { items } = this.props;

    return items.map(item => ({
      label: item.label,
      identifier: item.identifier,
      icon: item.icon || null,
      hash: item.hash || null,
      data: item.data || {},
      tooltip: item.tooltip || null,
      popover: item.popover || null,
      className: item.className || undefined,
      activeClassName: item.activeClassName || undefined,
      isDisabled: item.isDisabled || false,
      onClose: item.onClose
    }));
  }

  handleItemClick = (event: SyntheticEvent<HTMLLIElement>): void => {
    const { onClick } = this.props;
    const target = event.currentTarget;
    const identifier = target.dataset.identifier || null;
    const isActive = target.dataset.active === 'true';
    const type = target.dataset.type || null;
    const hash = target.dataset.hash || null;

    onClick(identifier, type, isActive, hash);
  };

  renderPopover(item: TabsSectionItem): React.ReactNode | undefined | null {
    return (
      <Popover
        classNamePopover="tabs-section__popover"
        triggerComponent={item.label}
      >
        {item.popover}
      </Popover>
    );
  }

  renderTooltip(tooltip: TabsSectionTooltip): React.ReactNode | undefined | null {
    const { size } = this.props;

    const tooltipClasses = classnames('tabs-section__tooltip', {
      [`tabs-section__tooltip--size-${size}`]: size
    });

    return tooltip.message && (
      <div className={tooltipClasses}>
        <Tooltip
          message={tooltip.message}
          width={tooltip.width || 'normal'}
          showQuestionMark
        />
      </div>
    );
  }

  renderIcon(item: TabsSectionItem): React.ReactNode {
    const icon = item.icon;

    const iconProps = {
      name: icon?.name || '',
      width: icon?.width || '',
      height: icon?.height || '',
      link: icon?.url || '',
      clickable: Boolean(icon?.url),
      openInNewTab: Boolean(icon?.url) || false,
      onClick: (event) => event.stopPropagation()
    };

    if (icon?.isDynamic) {
      return (
        <div className="tabs-section__icon-trigger">
          <SVGDynamicIcon
            {...iconProps}
            customColor={icon.color || 'black'}
          />
        </div>
      );
    }

    return (
      <div className="tabs-section__icon-trigger">
        <Tooltip
          message={`${__t(`${COMMON_MSG_KEY}.basics.open`)} ${__t(`${COMMON_MSG_KEY}.social.${item.identifier}`)}`}
          width="auto-compact"
        >
          <SVGIcon
            {...iconProps}
          />
        </Tooltip>
      </div>
    );
  }

  renderItemContents(item: TabsSectionItem): React.ReactNode {
    return (
      <React.Fragment>
        {
          item.icon && (
            <div className="tabs-section__icon">
              {this.renderIcon(item)}
            </div>
          )
        }
        {
          !isNil(item.popover)
            ? this.renderPopover(item)
            : item.label
        }
        {
          item.tooltip &&
            this.renderTooltip(item.tooltip)
        }
        {
          item.onClose && (
            <div className="tabs-section__close">
              <SVGDynamicIcon
                name="cross-custom-fg"
                width={12}
                height={12}
                onClick={item.onClose}
                customColor="grey"
              />
            </div>
          )
        }
      </React.Fragment>
    );
  }

  renderItem(item: TabsSectionItem): React.ReactNode {
    const { items, activeTab, size, isDisabled, clickableActiveTab } = this.props;
    const isItemDisabled = isDisabled || item.isDisabled;
    const isActive = item.identifier === activeTab;

    const data = {
      ...item.data,
      identifier: item.identifier,
      active: item.identifier === activeTab,
      hash: item.hash
    };

    const itemClasses = classnames('tabs-section__item', item.className, {
      [item.activeClassName || '']: item.activeClassName && isActive,
      'tabs-section__item--active': isActive && items.length > 1,
      'tabs-section__item--active-single': items.length === 1,
      'tabs-section__item--is-disabled': isItemDisabled,
      'tabs-section__item--has-popover': !isNil(item.popover),
      'tabs-section__item--clickable': !isActive || isActive && (clickableActiveTab || item.tooltip) || false,
      [`tabs-section__item--size-${size}`]: size
    });

    return (
      <li
        key={item.identifier}
        className={itemClasses}
        {...expandDataProps(data)}
        onClick={!isItemDisabled && (!isActive || clickableActiveTab)
          ? this.handleItemClick
          : undefined}
      >
        {this.renderItemContents(item)}
      </li>
    );
  }

  render(): React.ReactNode {
    const { withShadow, className } = this.props;
    const updatedItems = this.getUpdatedItems();
    const wrapperClasses = classnames('tabs-section', className, {
      'tabs-section--with-shadow': withShadow
    });

    return (
      <ul className={wrapperClasses}>
        {
          updatedItems.map(item => (
            this.renderItem(item)
          ))
        }
      </ul>
    );
  }
}

export default TabsSection;
